import type { TrainingScorePoint } from '../training';

interface TrendChartProps {
  label: string;
  points: readonly TrainingScorePoint[];
  emphasized?: boolean;
}

export function TrendChart({ label, points, emphasized = false }: TrendChartProps) {
  const width = emphasized ? 960 : 480;
  const height = 190;
  const padX = 36;
  const padTop = 22;
  const padBottom = 34;
  const times = points.map((point) => new Date(point.date).getTime());
  const firstTime = times[0] ?? 0;
  const lastTime = times.at(-1) ?? firstTime;
  const timeSpan = Math.max(1, lastTime - firstTime);
  const coordinates = points.map((point, index) => ({
    ...point,
    x: points.length === 1 ? width / 2 : padX + ((times[index] - firstTime) / timeSpan) * (width - padX * 2),
    y: padTop + ((100 - point.value) / 100) * (height - padTop - padBottom),
  }));
  const segments = coordinates.reduce<Array<typeof coordinates>>((groups, point) => {
    const current = groups.at(-1);
    if (
      !current?.length
      || current[0].environmentConditionId !== point.environmentConditionId
      || current[0].protocolVersion !== point.protocolVersion
    ) groups.push([point]);
    else current.push(point);
    return groups;
  }, []);
  const dateFormatter = new Intl.DateTimeFormat('ja-JP', { month: 'numeric', day: 'numeric' });
  const latest = points.at(-1);

  return (
    <article className={`trend-card${emphasized ? ' trend-card-wide' : ''}`}>
      <header>
        <span className="panel-label">{label}</span>
        <strong>{latest ? `${Math.round(latest.value)} / 100` : '期間内の記録なし'}</strong>
      </header>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label}のスコア推移`}>
        {[0, 25, 50, 75, 100].map((value) => {
          const y = padTop + ((100 - value) / 100) * (height - padTop - padBottom);
          return <g key={value}><line className="trend-grid" x1={padX} y1={y} x2={width - padX} y2={y} /><text className="trend-axis-label" x={4} y={y + 4}>{value}</text></g>;
        })}
        {coordinates.slice(1).map((point, index) => (
          point.environmentConditionId !== coordinates[index].environmentConditionId || point.protocolVersion !== coordinates[index].protocolVersion
            ? <line key={`break-${point.sessionId}`} className="trend-environment-break" x1={point.x} y1={padTop} x2={point.x} y2={height - padBottom} />
            : null
        ))}
        {segments.map((segment, segmentIndex) => {
          const path = segment.map((point, index) => `${index ? 'L' : 'M'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
          return <path key={`${segment[0]?.sessionId}-${segmentIndex}`} className="trend-line" d={path} />;
        })}
        {coordinates.map((point) => <g key={point.sessionId}><circle className="trend-point" cx={point.x} cy={point.y} r="4" /><title>{`${dateFormatter.format(new Date(point.date))}：${point.value.toFixed(1)}`}</title></g>)}
        {points.length > 0 && <>
          <text className="trend-date-label" x={padX} y={height - 8}>{dateFormatter.format(new Date(points[0].date))}</text>
          {points.length > 1 && <text className="trend-date-label" textAnchor="end" x={width - padX} y={height - 8}>{dateFormatter.format(new Date(points.at(-1)!.date))}</text>}
        </>}
      </svg>
      <footer><span>{points.length} RECORDS</span><span>0–100</span></footer>
    </article>
  );
}
